import { useState, useEffect, useRef } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import type { ActiveTimer } from '../types/activeTimer';

/**
 * Hook para escuchar timers activos de una familia en tiempo real
 * Sincroniza automáticamente entre todos los dispositivos/tabs
 */
export function useGlobalActivityTimers(familyId: string | null) {
  const [timers, setTimers] = useState<ActiveTimer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    if (!familyId) {
      setTimers([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'activeTimers'),
      where('familyId', '==', familyId)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!isMountedRef.current) return;
        const timersData: ActiveTimer[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as ActiveTimer[];

        setTimers(timersData);
        setLoading(false);
        setError(null);
      },
      (err) => {
        if (!isMountedRef.current) return;
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => {
      isMountedRef.current = false;
      try {
        unsubscribe();
      } catch (e) {
        // Ignore errors when listener is already closed
      }
    };
  }, [familyId]);

  return { timers, loading, error };
}

/**
 * Hook para obtener timers de un miembro específico
 */
export function useMemberTimers(familyId: string | null, memberId: string | null) {
  const { timers, loading, error } = useGlobalActivityTimers(familyId);

  const memberTimers = timers.filter(t => t.memberId === memberId);

  return { timers: memberTimers, loading, error };
}

/**
 * Hook para obtener un timer específico por actividad y miembro
 */
export function useActivityTimer(
  familyId: string | null,
  memberId: string | null,
  activityId: string | null
) {
  const { timers, loading, error } = useGlobalActivityTimers(familyId);

  const timer = timers.find(
    t => t.memberId === memberId && t.activityId === activityId
  ) || null;

  return { timer, loading, error };
}
